import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 no-underline group">
          <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className="ri-contrast-2-fill text-white text-lg"></i>
          </span>
          <span className="font-heading text-lg font-bold text-foreground-950 whitespace-nowrap">
            {t('siteName')}
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link
            to="/"
            className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
              isActive('/')
                ? 'text-primary-600'
                : 'text-foreground-600 hover:text-foreground-950'
            }`}
          >
            {t('navHome')}
          </Link>
          <Link
            to="/category/contrast-visual-design"
            className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
              location.pathname.startsWith('/category')
                ? 'text-primary-600'
                : 'text-foreground-600 hover:text-foreground-950'
            }`}
          >
            {t('navCategories')}
          </Link>
          <Link
            to="/about"
            className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
              isActive('/about')
                ? 'text-primary-600'
                : 'text-foreground-600 hover:text-foreground-950'
            }`}
          >
            {t('navAbout')}
          </Link>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 flex flex-col gap-3">
          <Link
            to="/"
            className={`text-sm font-medium px-3 py-2 rounded-lg whitespace-nowrap transition-colors ${
              isActive('/')
                ? 'bg-primary-100 text-primary-700'
                : 'text-foreground-600 hover:bg-background-100'
            }`}
          >
            {t('navHome')}
          </Link>
          <Link
            to="/category/contrast-visual-design"
            className={`text-sm font-medium px-3 py-2 rounded-lg whitespace-nowrap transition-colors ${
              location.pathname.startsWith('/category')
                ? 'bg-primary-100 text-primary-700'
                : 'text-foreground-600 hover:bg-background-100'
            }`}
          >
            {t('navCategories')}
          </Link>
          <Link
            to="/about"
            className={`text-sm font-medium px-3 py-2 rounded-lg whitespace-nowrap transition-colors ${
              isActive('/about')
                ? 'bg-primary-100 text-primary-700'
                : 'text-foreground-600 hover:bg-background-100'
            }`}
          >
            {t('navAbout')}
          </Link>
        </div>
      )}
    </nav>
  );
}